import { useMemo, useState } from 'react';

import { matches } from '../../shared/translit';

/**
 * Выбор одного значения из длинного списка (T11.5).
 *
 * Марок четыреста двадцать четыре. Выпадающий список из такого числа —
 * это прокрутка на минуту с риском проскочить нужное, и человек бросает
 * подбор раньше, чем находит свою марку. Поэтому здесь строка поиска, а
 * список показывает только подходящее.
 *
 * ## Показывается не всё, что нашлось
 *
 * Пустой запрос дал бы все четыреста двадцать четыре кнопки — ту же
 * прокрутку, только без выпадающего списка. Показывается дюжина, и
 * этого хватает: человек, знающий свою марку, наберёт две буквы и
 * увидит её сразу.
 *
 * ## Выбранное остаётся видимым
 *
 * Оно показано отдельной строкой над поиском: иначе после выбора экран
 * выглядел бы так же, как до него, и человек не понял бы, сработало ли
 * нажатие.
 */

export interface Option {
  readonly id: number;
  readonly name: string;
  /** Пояснение рядом с именем: годы поколения, страна марки. */
  readonly note?: string | undefined;
}

/** Сколько совпадений показывать. Больше — снова прокрутка. */
const SHOWN = 12;

export function Picker({
  title,
  options,
  value,
  values,
  placeholder,
  emptyLabel,
  disabled = false,
  onPick,
}: {
  title: string;
  options: readonly Option[];
  /** Выбранное при выборе одного. */
  value?: number | undefined;
  /**
   * Выбранные при выборе нескольких.
   *
   * Второй довод вместо признака «множественный»: наличие списка само
   * говорит, что их может быть много, и не даёт задать оба вида разом.
   */
  values?: readonly number[] | undefined;
  placeholder: string;
  /** Что написано, когда не выбрано ничего: «любая», «любое». */
  emptyLabel: string;
  disabled?: boolean;
  onPick: (id: number | undefined) => void;
}): React.JSX.Element {
  const [query, setQuery] = useState('');
  const many = values !== undefined;
  const picked = many ? values : value === undefined ? [] : [value];
  const chosen = many ? undefined : options.find((item) => item.id === value);
  // При выборе нескольких уже выбранные из списка убираются: предлагать
  // добавить то, что уже добавлено, — предлагать пустое действие
  const offered = many ? options.filter((item) => !picked.includes(item.id)) : options;

  const found = useMemo(() => {
    const needle = query.trim();

    if (needle === '') return offered.slice(0, SHOWN);

    // Сверка понимает русское написание: «БМВ» находит `BMW`. Человек
    // знает марку по-русски, а справочник записан латиницей, и раньше
    // между ними не было ни одного общего знака
    return offered.filter((item) => matches(item.name, needle)).slice(0, SHOWN);
  }, [offered, query]);

  return (
    <div className="field">
      <span className="field__title">{title}</span>

      {/*
        Выбранное — слева от поиска, а не строкой над ним.
        Отдельной строкой оно читалось как заголовок, а не как то, что
        можно нажать и сбросить; да и место занимало вдвое больше.

        Пока не выбрано ничего, здесь «любая» — и это не украшение:
        человек должен видеть, что поле не пустое по недосмотру, а
        означает «подойдёт любая».
      */}
      <div className="field__row">
        <button
          type="button"
          className="picked"
          disabled={disabled || picked.length === 0}
          onClick={() => {
            onPick(undefined);
            setQuery('');
          }}
        >
          {picked.length === 0
            ? emptyLabel
            : many
              ? 'сбросить все'
              : `${chosen?.name ?? ''} · сбросить`}
        </button>

        {disabled || options.length === 0 || (!many && chosen !== undefined) ? null : (
          <input
            className="input field__search"
            placeholder={placeholder}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
        )}
      </div>

      {/*
        Пустой справочник — не то же, что «ничего не нашлось». Показать
        здесь строку поиска значило бы предложить искать в пустоте:
        человек наберёт название, ничего не увидит и решит, что сломался
        поиск, а не что данных ещё нет.
      */}
      {!disabled && picked.length === 0 && options.length === 0 ? (
        <span className="field__title">Справочник ещё не заполнен</span>
      ) : null}

      {/* Выбранные при множественном выборе: каждый снимается отдельно */}
      {many && picked.length > 0 ? (
        <div className="chips">
          {picked.map((id) => {
            const item = options.find((each) => each.id === id);

            return (
              <button
                key={id}
                type="button"
                className="chip chip--picked"
                onClick={() => {
                  onPick(id);
                }}
              >
                {item?.name ?? id} ×
              </button>
            );
          })}
        </div>
      ) : null}

      {disabled || (!many && chosen !== undefined) || options.length === 0 ? null : (
        <>
          <div className="chips chips--grid">
            {found.map((one) => (
              <button
                key={one.id}
                type="button"
                className="chip"
                onClick={() => {
                  onPick(one.id);
                  setQuery('');
                }}
              >
                {one.name}
                {one.note === undefined ? null : <span className="chip__note"> {one.note}</span>}
              </button>
            ))}
            {found.length === 0 ? <span className="field__title">Ничего не найдено</span> : null}
          </div>
        </>
      )}
    </div>
  );
}
